import type { RulePolicyBinding } from "../../domain/rulePolicy";
import { DEMO_PROJECTION_POLICY_REGISTRY } from "./demoProjectionPolicyRegistry";
import { EVALUATION_POLICY_REGISTRY } from "./evaluationPolicyRegistry";
import { INPUT_SCHEMA_REGISTRY } from "./inputSchemaRegistry";
import { ROUTING_POLICY_REGISTRY } from "./routingPolicyRegistry";
import { ROW_MAPPER_REGISTRY } from "./rowMapperRegistry";
import { SUMMARY_POLICY_REGISTRY } from "./summaryPolicyRegistry";

export const RULE_POLICY_BINDINGS: Record<string, RulePolicyBinding> = {
    "DQ-001": {
        ruleId: "DQ-001",
        executableFamily: "statement_rule",
        inputSchemaId: "enriched_part.publish_gate",
        rowMapperId: "identity.enriched_part",
        evaluationPolicyId: "dq001.mandatory_attribute_completeness",
        routingPolicyId: "dq001.quarantine_incomplete",
        summaryPolicyId: "statement.required_fields",
        demoProjectionPolicyId: "none",
    },
    "DQ-002": {
        ruleId: "DQ-002",
        executableFamily: "statement_rule",
        inputSchemaId: "enriched_part.publish_gate",
        rowMapperId: "identity.enriched_part",
        evaluationPolicyId: "dq002.unit_of_measure_consistency",
        routingPolicyId: "dq002.review_uom_conflict",
        summaryPolicyId: "statement.passive_uom_conflict",
        demoProjectionPolicyId: "none",
    },
    "DQ-003": {
        ruleId: "DQ-003",
        executableFamily: "statement_rule",
        inputSchemaId: "enriched_part.publish_gate",
        rowMapperId: "identity.enriched_part",
        evaluationPolicyId: "dq003.lifecycle_status_gating",
        routingPolicyId: "dq003.archive_lifecycle_blocked",
        summaryPolicyId: "statement.blocked_values",
        demoProjectionPolicyId: "none",
    },
    "DQ-004": {
        ruleId: "DQ-004",
        executableFamily: "statement_rule",
        inputSchemaId: "enriched_part.publish_gate",
        rowMapperId: "identity.enriched_part",
        evaluationPolicyId: "dq004.mpn_format_validation",
        routingPolicyId: "dq004.quarantine_suspicious_mpn",
        summaryPolicyId: "statement.mpn_format",
        demoProjectionPolicyId: "none",
    },
    "CF-013": {
        ruleId: "CF-013",
        executableFamily: "statement_rule",
        inputSchemaId: "enriched_part.publish_gate",
        rowMapperId: "identity.enriched_part",
        evaluationPolicyId: "statement.numeric_threshold",
        routingPolicyId: "cf013.confidence_threshold_result",
        summaryPolicyId: "statement.numeric_threshold",
        demoProjectionPolicyId: "none",
    },
    "TR-005": {
        ruleId: "TR-005",
        executableFamily: "value_table",
        inputSchemaId: "taxonomy_path.lookup",
        rowMapperId: "identity.enriched_part",
        evaluationPolicyId: "tr005.taxonomy_value_table_lookup",
        routingPolicyId: "tr005.value_table_result",
        summaryPolicyId: "value_table.taxonomy_mapping",
        demoProjectionPolicyId: "taxonomyMappings",
    },
    "TR-006": {
        ruleId: "TR-006",
        executableFamily: "text_merge",
        inputSchemaId: "text_merge.part_preview",
        rowMapperId: "identity.text_merge_part_demo_row",
        evaluationPolicyId: "tr006.erp_description_text_merge",
        routingPolicyId: "tr006.description_result",
        summaryPolicyId: "text_merge.erp_description",
        demoProjectionPolicyId: "none",
    },
    "TR-007": {
        ruleId: "TR-007",
        executableFamily: "text_merge",
        inputSchemaId: "text_merge.part_preview",
        rowMapperId: "identity.text_merge_part_demo_row",
        evaluationPolicyId: "tr007.crm_product_name_text_merge",
        routingPolicyId: "tr007.product_name_result",
        summaryPolicyId: "text_merge.crm_product_name",
        demoProjectionPolicyId: "none",
    },
    "TR-008": {
        ruleId: "TR-008",
        executableFamily: "rule_table",
        inputSchemaId: "supplier_price_update.tolerance",
        rowMapperId: "identity.supplier_price_update",
        evaluationPolicyId: "tr008.price_tolerance_rule_table",
        routingPolicyId: "tr008.price_tolerance_result",
        summaryPolicyId: "rule_table.price_tolerance",
        demoProjectionPolicyId: "priceToleranceRules",
    },
    "WF-009": {
        ruleId: "WF-009",
        executableFamily: "rule_table",
        inputSchemaId: "workflow.new_part_catalog_lookup",
        rowMapperId: "identity.catalog_decision_rule_table_row",
        evaluationPolicyId: "rule_table.catalog_decision",
        routingPolicyId: "rule_table.catalog_decision_result",
        summaryPolicyId: "rule_table.catalog_decision",
        demoProjectionPolicyId: "none",
    },
    "WF-010": {
        ruleId: "WF-010",
        executableFamily: "rule_table",
        inputSchemaId: "workflow.price_alert",
        rowMapperId: "identity.catalog_decision_rule_table_row",
        evaluationPolicyId: "rule_table.catalog_decision",
        routingPolicyId: "rule_table.catalog_decision_result",
        summaryPolicyId: "rule_table.catalog_decision",
        demoProjectionPolicyId: "none",
    },
    "WF-011": {
        ruleId: "WF-011",
        executableFamily: "rule_table",
        inputSchemaId: "workflow.obsolescence_alert",
        rowMapperId: "identity.catalog_decision_rule_table_row",
        evaluationPolicyId: "rule_table.catalog_decision",
        routingPolicyId: "rule_table.catalog_decision_result",
        summaryPolicyId: "rule_table.catalog_decision",
        demoProjectionPolicyId: "none",
    },
    "WF-012": {
        ruleId: "WF-012",
        executableFamily: "rule_table",
        inputSchemaId: "workflow.compliance_change",
        rowMapperId: "identity.catalog_decision_rule_table_row",
        evaluationPolicyId: "rule_table.catalog_decision",
        routingPolicyId: "rule_table.catalog_decision_result",
        summaryPolicyId: "rule_table.catalog_decision",
        demoProjectionPolicyId: "none",
    },
    "CF-014": {
        ruleId: "CF-014",
        executableFamily: "rule_table",
        inputSchemaId: "source_priority.override",
        rowMapperId: "identity.catalog_decision_rule_table_row",
        evaluationPolicyId: "rule_table.catalog_decision",
        routingPolicyId: "rule_table.catalog_decision_result",
        summaryPolicyId: "rule_table.catalog_decision",
        demoProjectionPolicyId: "none",
    },
};

export type RulePolicyBindingIssue = {
    ruleId: string;
    message: string;
};

export const getRegisteredRulePolicyBindings = (): Record<string, RulePolicyBinding> => RULE_POLICY_BINDINGS;

export const getRulePolicyBinding = (ruleId: string): RulePolicyBinding | undefined => RULE_POLICY_BINDINGS[ruleId];

export const requireRulePolicyBinding = (ruleId: string): RulePolicyBinding => {
    const binding = getRulePolicyBinding(ruleId);
    if (!binding) throw new Error(`Missing rule policy binding for ${ruleId}.`);
    return binding;
};

export const validateRulePolicyBindings = (
    bindings: Record<string, RulePolicyBinding> = RULE_POLICY_BINDINGS,
): RulePolicyBindingIssue[] =>
    Object.values(bindings).flatMap((binding) => {
        const issues: RulePolicyBindingIssue[] = [];

        if (binding.ruleId !== Object.keys(bindings).find((ruleId) => bindings[ruleId] === binding)) {
            issues.push({
                ruleId: binding.ruleId,
                message: "Policy binding ruleId must match its registry key.",
            });
        }

        if (!INPUT_SCHEMA_REGISTRY[binding.inputSchemaId]) {
            issues.push({ ruleId: binding.ruleId, message: `Unknown input schema ${binding.inputSchemaId}.` });
        }
        if (!ROW_MAPPER_REGISTRY[binding.rowMapperId]) {
            issues.push({ ruleId: binding.ruleId, message: `Unknown row mapper ${binding.rowMapperId}.` });
        }
        if (!EVALUATION_POLICY_REGISTRY[binding.evaluationPolicyId]) {
            issues.push({
                ruleId: binding.ruleId,
                message: `Unknown evaluation policy ${binding.evaluationPolicyId}.`,
            });
        }
        if (!ROUTING_POLICY_REGISTRY[binding.routingPolicyId]) {
            issues.push({ ruleId: binding.ruleId, message: `Unknown routing policy ${binding.routingPolicyId}.` });
        }
        if (!SUMMARY_POLICY_REGISTRY[binding.summaryPolicyId]) {
            issues.push({ ruleId: binding.ruleId, message: `Unknown summary policy ${binding.summaryPolicyId}.` });
        }
        if (!DEMO_PROJECTION_POLICY_REGISTRY[binding.demoProjectionPolicyId]) {
            issues.push({
                ruleId: binding.ruleId,
                message: `Unknown demo projection policy ${binding.demoProjectionPolicyId}.`,
            });
        }

        return issues;
    });
